/**
 * 実行速度制御のための高度なコントローラー
 */
export class SpeedController {
  private slider: HTMLInputElement
  private valueDisplay: HTMLSpanElement
  private speedPresets: HTMLElement
  private customSpeedInput: HTMLInputElement | null = null
  
  // 速度プリセット（ミリ秒）
  private readonly presets = {
    slow: { value: 2000, label: '遅い', sliderValue: 1 },
    normal: { value: 500, label: '通常', sliderValue: 5 },
    fast: { value: 100, label: '速い', sliderValue: 8 },
    instant: { value: 10, label: '最速', sliderValue: 10 },
  }
  
  private onSpeedChange?: (speed: number) => void
  
  constructor() {
    this.slider = document.getElementById('speed-slider') as HTMLInputElement
    this.valueDisplay = document.getElementById('speed-value') as HTMLSpanElement
    
    // 速度プリセットボタンを作成
    this.speedPresets = this.createSpeedPresets()
    
    this.setupStyles()
    this.setupEventListeners()
    this.updateDisplay()
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .speed-control {
        position: relative;
      }
      
      .speed-control__presets {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 0.5rem;
        display: flex;
        gap: 0.25rem;
        background: var(--surface-color);
        padding: 0.5rem;
        border-radius: 0.375rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        opacity: 0;
        visibility: hidden;
        transform: translateY(-10px);
        transition: all 0.2s ease;
      }
      
      .speed-control:hover .speed-control__presets,
      .speed-control:focus-within .speed-control__presets {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
      
      .speed-preset {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
        border: 1px solid var(--border-color);
        border-radius: 0.25rem;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
      }
      
      .speed-preset:hover {
        background: var(--primary-color);
        color: white;
        border-color: var(--primary-color);
      }
      
      .speed-preset.active {
        background: var(--primary-color);
        color: white;
        border-color: var(--primary-color);
      }
      
      /* カスタムスライダースタイル */
      #speed-slider {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 6px;
        border-radius: 3px;
        background: #e5e7eb;
        outline: none;
        transition: background 0.3s ease;
      }
      
      #speed-slider:hover {
        background: #d1d5db;
      }
      
      #speed-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: var(--primary-color);
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
      
      #speed-slider::-webkit-slider-thumb:hover {
        transform: scale(1.2);
        box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
      }
      
      #speed-slider::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: var(--primary-color);
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
      
      #speed-slider::-moz-range-thumb:hover {
        transform: scale(1.2);
        box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
      }
      
      /* 速度インジケーター */
      .speed-indicator {
        position: absolute;
        bottom: 100%;
        left: var(--position);
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        font-size: 0.75rem;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        margin-bottom: 0.5rem;
      }
      
      #speed-slider:hover ~ .speed-indicator,
      #speed-slider:focus ~ .speed-indicator {
        opacity: 1;
      }
      
      /* 速度値の表示 */
      #speed-value {
        font-weight: 600;
        color: var(--primary-color);
        min-width: 2rem;
        text-align: center;
        position: relative;
      }
      
      #speed-value::after {
        content: 'x';
        font-size: 0.875em;
        font-weight: normal;
        margin-left: 0.125rem;
      }
      
      /* カスタム速度入力 */
      .custom-speed-input {
        position: absolute;
        top: 100%;
        right: 0;
        margin-top: 0.5rem;
        display: none;
        background: white;
        border: 1px solid var(--border-color);
        border-radius: 0.375rem;
        padding: 0.5rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
      
      .custom-speed-input.visible {
        display: block;
      }
      
      .custom-speed-input input {
        width: 100px;
        padding: 0.25rem 0.5rem;
        border: 1px solid var(--border-color);
        border-radius: 0.25rem;
        font-size: 0.875rem;
      }
      
      .custom-speed-input button {
        margin-left: 0.25rem;
        padding: 0.25rem 0.5rem;
        background: var(--primary-color);
        color: white;
        border: none;
        border-radius: 0.25rem;
        font-size: 0.875rem;
        cursor: pointer;
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * 速度プリセットボタンを作成
   */
  private createSpeedPresets(): HTMLElement {
    const container = document.createElement('div')
    container.className = 'speed-control__presets'
    
    Object.entries(this.presets).forEach(([key, preset]) => {
      const button = document.createElement('button')
      button.className = 'speed-preset'
      button.textContent = preset.label
      button.setAttribute('data-preset', key)
      button.setAttribute('data-value', String(preset.sliderValue))
      
      button.addEventListener('click', () => {
        this.slider.value = String(preset.sliderValue)
        this.updateDisplay()
        this.notifySpeedChange()
      })
      
      container.appendChild(button)
    })
    
    // カスタム速度ボタンを追加
    const customButton = document.createElement('button')
    customButton.className = 'speed-preset'
    customButton.textContent = 'カスタム'
    customButton.addEventListener('click', () => {
      this.showCustomSpeedInput()
    })
    container.appendChild(customButton)
    
    // 速度コントロールの親要素に追加
    const speedControl = this.slider.closest('.speed-control')
    if (speedControl) {
      speedControl.classList.add('speed-control')
      speedControl.appendChild(container)
    }
    
    return container
  }
  
  /**
   * イベントリスナーを設定
   */
  private setupEventListeners(): void {
    // スライダーの値変更
    this.slider.addEventListener('input', () => {
      this.updateDisplay()
      this.notifySpeedChange()
    })
    
    // キーボードショートカット
    document.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }
      
      // Shift + 上矢印: 速度を上げる
      if (e.shiftKey && e.key === 'ArrowUp') {
        e.preventDefault()
        const newValue = Math.min(10, parseInt(this.slider.value) + 1)
        this.slider.value = String(newValue)
        this.updateDisplay()
        this.notifySpeedChange()
      }
      
      // Shift + 下矢印: 速度を下げる
      if (e.shiftKey && e.key === 'ArrowDown') {
        e.preventDefault()
        const newValue = Math.max(1, parseInt(this.slider.value) - 1)
        this.slider.value = String(newValue)
        this.updateDisplay()
        this.notifySpeedChange()
      }
    })
    
    // 速度インジケーターを追加
    this.addSpeedIndicator()
  }
  
  /**
   * 速度インジケーターを追加
   */
  private addSpeedIndicator(): void {
    const indicator = document.createElement('div')
    indicator.className = 'speed-indicator'
    
    const updateIndicator = () => {
      const value = parseInt(this.slider.value)
      const percentage = ((value - 1) / 9) * 100
      indicator.style.setProperty('--position', `${percentage}%`)
      
      const speed = this.calculateSpeed(value)
      indicator.textContent = `${speed}ms/ステップ`
    }
    
    this.slider.addEventListener('input', updateIndicator)
    this.slider.parentElement?.appendChild(indicator)
    updateIndicator()
  }
  
  /**
   * 表示を更新
   */
  private updateDisplay(): void {
    const value = parseInt(this.slider.value)
    this.valueDisplay.textContent = String(value)
    
    // アクティブなプリセットをハイライト
    this.updateActivePreset()
    
    // アニメーション効果
    this.valueDisplay.style.transform = 'scale(1.2)'
    setTimeout(() => {
      this.valueDisplay.style.transform = ''
    }, 100)
  }
  
  /**
   * アクティブなプリセットを更新
   */
  private updateActivePreset(): void {
    const value = parseInt(this.slider.value)
    const presetButtons = this.speedPresets.querySelectorAll('.speed-preset')
    
    presetButtons.forEach(button => {
      const presetValue = button.getAttribute('data-value')
      if (presetValue && parseInt(presetValue) === value) {
        button.classList.add('active')
      } else {
        button.classList.remove('active')
      }
    })
  }
  
  /**
   * カスタム速度入力を表示
   */
  private showCustomSpeedInput(): void {
    if (!this.customSpeedInput) {
      this.customSpeedInput = this.createCustomSpeedInput()
    }
    
    this.customSpeedInput.classList.add('visible')
    const input = this.customSpeedInput.querySelector('input') as HTMLInputElement
    input.focus()
    input.select()
  }
  
  /**
   * カスタム速度入力を作成
   */
  private createCustomSpeedInput(): any {
    const container = document.createElement('div')
    container.className = 'custom-speed-input'
    
    const input = document.createElement('input')
    input.type = 'number'
    input.min = '10'
    input.max = '5000'
    input.placeholder = 'ミリ秒'
    input.value = String(this.calculateSpeed(parseInt(this.slider.value)))
    
    const button = document.createElement('button')
    button.textContent = '設定'
    
    const apply = () => {
      const ms = parseInt(input.value)
      if (ms >= 10 && ms <= 5000) {
        // ミリ秒からスライダー値に変換
        const sliderValue = this.millisecondsToSliderValue(ms)
        this.slider.value = String(sliderValue)
        this.updateDisplay()
        this.notifySpeedChange()
        container.classList.remove('visible')
      }
    }
    
    button.addEventListener('click', apply)
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        apply()
      }
    })
    
    container.appendChild(input)
    container.appendChild(button)
    
    const speedControl = this.slider.closest('.speed-control')
    speedControl?.appendChild(container)
    
    // クリック外で閉じる
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target as Node)) {
        container.classList.remove('visible')
      }
    })
    
    return container
  }
  
  /**
   * スライダー値から速度（ミリ秒）を計算
   */
  calculateSpeed(sliderValue: number): number {
    // 1: 2000ms, 5: 500ms, 10: 100ms
    return Math.max(100, 2000 / sliderValue)
  }
  
  /**
   * ミリ秒からスライダー値に変換
   */
  private millisecondsToSliderValue(ms: number): number {
    // 逆関数: sliderValue = 2000 / ms
    const value = 2000 / ms
    return Math.max(1, Math.min(10, Math.round(value)))
  }
  
  /**
   * 速度変更を通知
   */
  private notifySpeedChange(): void {
    if (this.onSpeedChange) {
      const speed = this.calculateSpeed(parseInt(this.slider.value))
      this.onSpeedChange(speed)
    }
  }
  
  /**
   * 速度変更リスナーを設定
   */
  setOnSpeedChange(callback: (speed: number) => void): void {
    this.onSpeedChange = callback
  }
  
  /**
   * 現在の速度を取得
   */
  getSpeed(): number {
    return this.calculateSpeed(parseInt(this.slider.value))
  }
}